/**
 *  <la-review-ticker>
 *  ------------------
 *  Shows this product's own Judge.me reviews one at a time, crossfading.
 *
 *  Judge.me's review-widget app block writes the product's first page of
 *  reviews into the page as `jdgm.data.reviewWidget[<product id>]`. This
 *  element only reads that object. Nothing qualifies, or no Judge.me data on
 *  the page -> the element stays [hidden] and the column is unchanged.
 *
 *  Rotation pauses on hover, on keyboard focus, while the tab is in the
 *  background, and when the customer presses pause. With reduced motion it
 *  never rotates on its own.
 */
if (!customElements.get('la-review-ticker')) {
  const SVG_NS = 'http://www.w3.org/2000/svg';
  const STAR_PATH =
    'M8 1.3l1.98 4.1 4.52.62-3.29 3.14.8 4.48L8 11.5l-4.01 2.14.8-4.48L1.5 6.02l4.52-.62Z';
  const CHECK_PATH = 'M3.6 8.4 6.5 11.2 12.4 5';

  class LaReviewTicker extends HTMLElement {
    connectedCallback() {
      if (this.built) return;
      if (this.build()) return;
      // The Judge.me data script sits further down the page. A deferred script
      // normally runs after it, but if it has not landed yet try once more.
      if (document.readyState !== 'complete') {
        window.addEventListener('load', () => this.build(), { once: true });
      }
    }

    disconnectedCallback() {
      this.stop();
      if (this.onVisibility) document.removeEventListener('visibilitychange', this.onVisibility);
    }

    /* ---------- data ---------- */

    widgetData() {
      const store = window.jdgm && window.jdgm.data && window.jdgm.data.reviewWidget;
      return store ? store[this.dataset.productId] : null;
    }

    /** Collapse whitespace; fall back to the title only when there is no body. */
    static text(review) {
      const raw = review.body || review.title || '';
      return String(raw).replace(/\s+/g, ' ').trim();
    }

    /** "Spellmon Quinn" -> "Spellmon Q."; anonymous reviewers keep Judge.me's label. */
    static displayName(review) {
      const name = String(review.reviewer_name || '').trim();
      if (!name || review.is_anonymous_reviewer) return name;
      const parts = name.split(/\s+/);
      if (parts.length === 1) return parts[0];
      return `${parts[0]} ${parts[parts.length - 1].charAt(0).toUpperCase()}.`;
    }

    /**
     * Star ratings alone are not enough: a 5-star review can still read
     * "fast service, but sadly I got the wrong lens". Reviews whose title or
     * body contains any word on the editor's skip list are left out, so the
     * ticker only ever shows unqualified praise. The full Judge.me widget
     * further down still shows every review.
     */
    skipPattern() {
      const words = (this.dataset.skipWords || '')
        .split(',')
        .map((word) => word.trim().toLowerCase().replace(/[’‘]/g, "'"))
        .filter(Boolean)
        .map((word) => word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
      return words.length ? new RegExp(`(^|[^a-z'])(${words.join('|')})(?![a-z])`, 'i') : null;
    }

    qualifying(reviews) {
      const minRating = parseInt(this.dataset.minRating, 10) || 5;
      const maxLength = parseInt(this.dataset.maxLength, 10) || 160;
      const skip = this.skipPattern();
      return reviews.filter((review) => {
        const text = LaReviewTicker.text(review);
        const haystack = `${review.title || ''} ${text}`.replace(/[’‘]/g, "'");
        return (
          Number(review.rating) >= minRating &&
          text.length > 0 &&
          text.length <= maxLength &&
          !(skip && skip.test(haystack))
        );
      });
    }

    /* ---------- build ---------- */

    build() {
      const data = this.widgetData();
      if (!data || !Array.isArray(data.reviews)) return false;
      this.built = true;

      const reviews = this.qualifying(data.reviews);
      if (!reviews.length) return true; // stays hidden

      this.stage = this.querySelector('.la-rt__stage');
      this.toggle = this.querySelector('.la-rt__toggle');
      this.slides = reviews.map((review) => this.slide(review));
      this.slides.forEach((slide) => this.stage.appendChild(slide));
      this.index = 0;
      this.slides[0].classList.add('is-active'); // visible before unhiding: no load-in fade
      this.slides.slice(1).forEach((slide) => slide.setAttribute('aria-hidden', 'true'));

      this.renderSummary(data);
      this.hidden = false;
      this.setupRotation();

      // No link (no Judge.me widget to jump to) and no toggle (one review):
      // drop the footer rule rather than leave it under nothing.
      const foot = this.querySelector('.la-rt__foot');
      if (foot && !foot.querySelector('.la-rt__all') && (!this.toggle || this.toggle.hidden)) {
        foot.hidden = true;
      }
      return true;
    }

    static svg(path, size, className, filled) {
      const svg = document.createElementNS(SVG_NS, 'svg');
      svg.setAttribute('width', size);
      svg.setAttribute('height', size);
      svg.setAttribute('viewBox', '0 0 16 16');
      svg.setAttribute('aria-hidden', 'true');
      if (className) svg.setAttribute('class', className);
      const p = document.createElementNS(SVG_NS, 'path');
      p.setAttribute('d', path);
      if (filled) {
        p.setAttribute('fill', 'currentColor');
      } else {
        p.setAttribute('fill', 'none');
        p.setAttribute('stroke', 'currentColor');
        p.setAttribute('stroke-width', '1.8');
        p.setAttribute('stroke-linecap', 'round');
        p.setAttribute('stroke-linejoin', 'round');
      }
      svg.appendChild(p);
      return svg;
    }

    slide(review) {
      const rating = Math.max(0, Math.min(5, Math.round(Number(review.rating) || 0)));

      const figure = document.createElement('figure');
      figure.className = 'la-rt__slide';

      const stars = document.createElement('div');
      stars.className = 'la-rt__stars';
      stars.setAttribute('role', 'img');
      stars.setAttribute('aria-label', `${rating} out of 5 stars`);
      for (let i = 1; i <= 5; i += 1) {
        stars.appendChild(
          LaReviewTicker.svg(STAR_PATH, 14, i <= rating ? 'is-on' : 'is-off', true)
        );
      }

      // A plain <p>, not <blockquote>: the theme styles `blockquote p` as an
      // uppercase, centred H3.
      const quote = document.createElement('p');
      quote.className = 'la-rt__quote';
      quote.textContent = LaReviewTicker.text(review);

      const by = document.createElement('figcaption');
      by.className = 'la-rt__by';
      const name = LaReviewTicker.displayName(review);
      if (name) {
        const who = document.createElement('span');
        who.className = 'la-rt__name';
        who.textContent = name;
        by.appendChild(who);
      }
      if (review.verified_buyer && this.dataset.verifiedLabel) {
        const badge = document.createElement('span');
        badge.className = 'la-rt__verified';
        badge.appendChild(LaReviewTicker.svg(CHECK_PATH, 12, '', false));
        badge.appendChild(document.createTextNode(this.dataset.verifiedLabel));
        by.appendChild(badge);
      }

      figure.appendChild(stars);
      figure.appendChild(quote);
      if (by.childNodes.length) figure.appendChild(by);
      return figure;
    }

    /** "3.7 * See all 3 reviews" -- the full picture is always one click away. */
    renderSummary(data) {
      const link = this.querySelector('.la-rt__all');
      if (!link) return;

      const target =
        document.querySelector('[id*="judge_me_reviews_review_widget"]') ||
        document.querySelector('[data-widget-name="review_widget"]');
      const count = parseInt(data.number_of_reviews, 10) || 0;
      if (!target || !count) {
        link.remove();
        return;
      }

      const average = parseFloat(data.average_rating);
      let label = (this.dataset.allLabel || '').replace(/\[count\]/g, count);
      if (count === 1) label = label.replace(/\breviews\b/, 'review');

      if (!Number.isNaN(average)) {
        const avg = document.createElement('b');
        avg.textContent = average.toFixed(1);
        link.appendChild(avg);
        link.appendChild(LaReviewTicker.svg(STAR_PATH, 11, 'la-rt__avg-star', true));
      }
      const text = document.createElement('span');
      text.textContent = label;
      link.appendChild(text);

      if (target.id) link.href = `#${target.id}`;
      link.addEventListener('click', (event) => {
        event.preventDefault();
        const smooth = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        target.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto', block: 'start' });
      });
    }

    /* ---------- rotation ---------- */

    setupRotation() {
      if (this.slides.length < 2) return;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      this.interval = Math.max(3, parseInt(this.dataset.interval, 10) || 6) * 1000;
      this.paused = new Set();

      this.addEventListener('mouseenter', () => this.pause('hover'));
      this.addEventListener('mouseleave', () => this.resume('hover'));
      this.addEventListener('focusin', () => this.pause('focus'));
      this.addEventListener('focusout', (event) => {
        if (!this.contains(event.relatedTarget)) this.resume('focus');
      });
      this.onVisibility = () => (document.hidden ? this.pause('tab') : this.resume('tab'));
      document.addEventListener('visibilitychange', this.onVisibility);

      // WCAG 2.2.2: anything that moves on its own for more than 5s needs a
      // way to stop it.
      if (this.toggle) {
        this.toggle.hidden = false;
        this.toggle.addEventListener('click', () => {
          if (this.paused.has('user')) this.resume('user');
          else this.pause('user');
        });
        this.updateToggle();
      }

      this.start();
    }

    pause(reason) {
      this.paused.add(reason);
      this.stop();
      if (reason === 'user') this.updateToggle();
    }

    resume(reason) {
      this.paused.delete(reason);
      if (reason === 'user') this.updateToggle();
      if (!this.paused.size) this.start();
    }

    start() {
      this.stop();
      this.timer = window.setInterval(() => this.show(this.index + 1), this.interval);
    }

    stop() {
      if (this.timer) window.clearInterval(this.timer);
      this.timer = null;
    }

    updateToggle() {
      const isPaused = this.paused.has('user');
      this.toggle.classList.toggle('is-paused', isPaused);
      this.toggle.setAttribute(
        'aria-label',
        (isPaused ? this.dataset.playLabel : this.dataset.pauseLabel) || ''
      );
    }

    show(next) {
      const index = (next + this.slides.length) % this.slides.length;
      if (index === this.index) return;
      const current = this.slides[this.index];
      const incoming = this.slides[index];
      current.classList.remove('is-active');
      current.setAttribute('aria-hidden', 'true');
      incoming.classList.add('is-active');
      incoming.removeAttribute('aria-hidden');
      this.index = index;
    }
  }

  customElements.define('la-review-ticker', LaReviewTicker);
}
